import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Drawer, Input, Select, Table, Tooltip } from 'antd';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  FaExclamationTriangle,
  FaFileAlt,
  FaShieldAlt,
  FaUserCheck,
  FaUsers,
} from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../entities/ConnectedUserEntity';
import { DetenteurEntity } from '../../../entities/detenteur.entity';
import { ORANGE, PRIMARY, YELLOW } from '../../../shared/colors';
import { ExportFormatButton } from '../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../shared/exportFiles';
import {
  getDetenteurAML,
  getDetenteurKYC,
} from '../../dashboard/components/Monitor/network';
import { BASE_MEDIA } from '../../shared/ApiRoutes';
import { Layout } from '../../shared/Layout';
import { exportDetenteurData } from '../clientele/components/exportDataExcel';
import { KYCForm } from '../emissionTitre/components/KYCForm';
import { StatusPill, StatusTone } from '../shared/StatusPill';
import { getDetenteurs } from '../emissionTitre/network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  > .content {
    width: 100%;
    padding: 12px;
  }

  .compliance-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .compliance-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .compliance-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.55;
  }

  .hero-actions,
  .toolbar-actions,
  .row-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .primary-action {
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
    font-family: Poppins-B;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .table-shell,
  .compliance-detail {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 82px;
    padding: 14px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 24px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(255, 176, 0, 0.26);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 340px) 1fr minmax(300px, 360px) auto;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
    min-width: min(100%, 360px);
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 8px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .filter-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .filter-tab {
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    padding: 8px 11px;
    background: var(--mt-surface);
    color: var(--mt-muted);
    cursor: pointer;
    font-family: Poppins-B;
    font-size: 12px;
  }

  .filter-tab.active {
    border-color: rgba(255, 176, 0, 0.55);
    color: var(--mt-warning);
    background: var(--mt-row-hover);
  }

  .advanced-filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    gap: 8px;
  }

  .advanced-filters .ant-select-selector {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 999px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
    box-shadow: none !important;
  }

  .advanced-filters .ant-select-selection-item,
  .advanced-filters .ant-select-selection-placeholder {
    color: var(--mt-text) !important;
  }

  .compliance-workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 430px;
    gap: 12px;
    margin-top: 12px;
  }

  .table-shell,
  .compliance-detail {
    overflow: hidden;
  }

  .table-title,
  .detail-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: #fff7e8;
    font-size: 11px;
    text-transform: none;
  }

  .client-name {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .client-name strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .client-name span,
  .muted {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .action-button {
    border-color: var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .action-button:hover {
    border-color: rgba(255, 176, 0, 0.55);
    color: var(--mt-warning);
  }

  .detail-body {
    display: grid;
    gap: 12px;
    padding: 14px;
  }

  .detail-head {
    display: grid;
    grid-template-columns: 54px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.08), transparent 42%),
      var(--mt-surface);
  }

  .client-avatar {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    border-radius: 8px;
    color: #07141c;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    font-family: Poppins-B;
    font-size: 20px;
  }

  .detail-head h3 {
    margin: 0;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .detail-row {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-surface);
  }

  .detail-row span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .detail-row strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-text);
    font-family: Poppins-B;
    overflow-wrap: anywhere;
  }

  .detail-row.full {
    grid-column: 1 / -1;
  }

  .detail-section {
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    overflow: hidden;
    background: var(--mt-card);
  }

  .detail-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 11px;
    border-bottom: 1px solid var(--mt-border);
    background: var(--mt-surface);
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .detail-section-title span {
    color: var(--mt-muted);
    font-family: Poppins-R;
    font-size: 11px;
    letter-spacing: 0;
    text-transform: none;
  }

  .detail-section-body {
    padding: 10px;
  }

  .risk-card {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .risk-metric {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-surface);
  }

  .risk-metric span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .risk-metric strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 14px;
  }

  .recommendation-box {
    border-left: 3px solid var(--mt-warning);
    border-radius: 8px;
    padding: 10px;
    background: rgba(255, 176, 0, 0.08);
    color: var(--mt-text);
    font-size: 12px;
    line-height: 1.45;
  }

  .document-list {
    display: grid;
    gap: 7px;
  }

  .document-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 8px 10px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .document-link a {
    color: var(--mt-warning);
    font-family: Poppins-B;
  }

  .empty-detail {
    display: grid;
    place-items: center;
    min-height: 310px;
    padding: 24px;
    color: var(--mt-muted);
    text-align: center;
  }

  .ant-table {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-family: Poppins-B;
    font-size: 11px;
    text-transform: uppercase;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .odd-row {
    background-color: var(--mt-row-odd);
  }

  .even-row {
    background-color: var(--mt-row-even);
  }

  .odd-row:hover > td,
  .even-row:hover > td,
  .selected-row > td {
    background-color: var(--mt-row-hover) !important;
    cursor: pointer;
  }

  .ant-drawer-content {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .ant-drawer-header {
    border-bottom-color: var(--mt-border);
    background: ${PRIMARY};
  }

  .ant-drawer-title {
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  @media (max-width: 760px) {
    > .content {
      padding: 10px;
    }

    .compliance-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid,
    .compliance-workspace,
    .advanced-filters,
    .risk-card {
      grid-template-columns: 1fr;
    }
  }
`;

type ComplianceFilter = 'all' | 'verified' | 'missing' | 'highRisk' | 'documents';

export const AdminKycPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [clients, setClients] = useState<DetenteurEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<ComplianceFilter>('all');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [residenceFilter, setResidenceFilter] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<DetenteurEntity>();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getDetenteurs(connectedUser.id)
      .then(async ({ data }) => {
        const enrichedClients: DetenteurEntity[] = await Promise.all(
          data.map(async (client: DetenteurEntity) => {
            const { data: kycData } = await getDetenteurKYC(client.id);
            const { data: amlData } = await getDetenteurAML(client.id);
            return {
              ...client,
              kyc: normalizeRecord(kycData),
              aml: normalizeRecord(amlData),
            };
          }),
        );
        setClients(enrichedClients);
        setSelectedClient(enrichedClients?.[0]);
      })
      .finally(() => setIsLoading(false));
  };

  const displayClients = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return clients.filter((client: any) => {
      const kycStatus = getKycStatus(client);
      const risk = getAmlRisk(client);
      const docs = getDocumentStatus(client);
      const searchable = [
        getClientName(client),
        client.code_referent,
        client.email,
        client.mobile,
        client.kyc?.nationality,
        client.kyc?.country_of_residence,
        risk.label,
        kycStatus.label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !term || searchable.includes(term);
      const matchesRisk = riskFilter === 'all' || risk.type === riskFilter;
      const matchesResidence =
        residenceFilter === 'all' ||
        client.kyc?.country_of_residence === residenceFilter;
      const matchesFilter =
        activeFilter === 'all' ||
        (activeFilter === 'verified' && kycStatus.type === 'verified') ||
        (activeFilter === 'missing' && kycStatus.type === 'missing') ||
        (activeFilter === 'highRisk' && risk.type === 'high') ||
        (activeFilter === 'documents' && docs.type !== 'complete');

      return matchesSearch && matchesFilter && matchesRisk && matchesResidence;
    });
  }, [activeFilter, clients, residenceFilter, riskFilter, searchTerm]);

  const residenceOptions = useMemo(() => {
    const options = clients
      .map((client: any) => client.kyc?.country_of_residence)
      .filter(Boolean)
      .map((country) => ({ label: country, value: country }));

    return getUniqueOptions(options);
  }, [clients]);

  const resetFilters = () => {
    setSearchTerm('');
    setActiveFilter('all');
    setRiskFilter('all');
    setResidenceFilter('all');
  };

  const stats = useMemo(() => {
    const verified = clients.filter(
      (client) => getKycStatus(client).type === 'verified',
    ).length;
    const missing = clients.filter(
      (client) => getKycStatus(client).type === 'missing',
    ).length;
    const highRisk = clients.filter(
      (client) => getAmlRisk(client).type === 'high',
    ).length;
    const docsMissing = clients.filter(
      (client) => getDocumentStatus(client).type !== 'complete',
    ).length;

    return {
      total: clients.length,
      verified,
      missing,
      highRisk,
      docsMissing,
    };
  }, [clients]);

  const columns = [
    {
      title: 'Client',
      render: (cell: any, row: DetenteurEntity) => (
        <div className='client-name'>
          <strong>{getClientName(row)}</strong>
          <span>{(row as any).code_referent || '--'}</span>
        </div>
      ),
    },
    {
      title: 'Statut KYC',
      render: (cell: any, row: any) => {
        const status = getKycStatus(row);
        return (
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        );
      },
    },
    {
      title: 'Score AML',
      render: (cell: any, row: any) => {
        const risk = getAmlRisk(row);
        return (
          <StatusPill tone={risk.tone}>{risk.score}</StatusPill>
        );
      },
    },
    {
      title: 'Niveau AML',
      render: (cell: any, row: any) => {
        const risk = getAmlRisk(row);
        return (
          <StatusPill tone={risk.tone}>{risk.label}</StatusPill>
        );
      },
    },
    {
      title: 'Documents',
      render: (cell: any, row: any) => {
        const docs = getDocumentStatus(row);
        return (
          <StatusPill tone={docs.tone}>
            {docs.completed}/{docs.total}
          </StatusPill>
        );
      },
    },
    {
      title: 'Résidence',
      render: (cell: any, row: any) => row.kyc?.country_of_residence || '--',
    },
    {
      title: 'PPE',
      render: (cell: any, row: any) =>
        row.kyc?.politically_exposed ? 'Oui' : 'Non',
    },
    {
      title: 'Action',
      render: (cell: any, row: DetenteurEntity) => (
        <div className='row-actions'>
          <Tooltip title='Vérifier le dossier KYC / AML' color={YELLOW}>
            <Button
              className='action-button'
              icon={<FaUserCheck />}
              onClick={(event) => {
                event.stopPropagation();
                setSelectedClient(row);
                setShowDrawer(true);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const upsertClient = (client: DetenteurEntity) => {
    setClients((current) =>
      current.some((item) => item.id === client.id)
        ? current.map((item) => (item.id === client.id ? { ...item, ...client } : item))
        : [...current, client],
    );
    setSelectedClient((current) =>
      current?.id === client.id ? { ...current, ...client } : current,
    );
  };

  return (
    <Layout>
      <Global styles={drawerStyles} />
      <Container>
        <div className='content'>
          <section className='compliance-hero'>
            <div>
              <span className='eyebrow'>Portefeuille client</span>
              <h1>KYC / AML Compliance Desk</h1>
              <p>
                Suivez les dossiers réglementaires, les documents d’identité, les
                scores AML et les prochaines actions de conformité.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='ghost-action'
                onExport={(format) => exportDetenteurData(clients, format, getExportMetadata(connectedUser))}
              />
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard icon={<FaUsers />} label='Dossiers total' value={stats.total} />
            <KpiCard
              icon={<FaShieldAlt />}
              label='KYC vérifiés'
              value={stats.verified}
            />
            <KpiCard
              icon={<FaExclamationTriangle />}
              label='KYC manquants'
              value={stats.missing}
            />
            <KpiCard
              icon={<FaExclamationTriangle />}
              label='AML risque élevé'
              value={stats.highRisk}
            />
            <KpiCard
              icon={<FaFileAlt />}
              label='Documents manquants'
              value={stats.docsMissing}
            />
          </section>

          <section className='toolbar'>
            <div className='search-box'>
              <FiSearch />
              <Input
                placeholder='Rechercher client, pays, statut, risque...'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className='filter-tabs'>
              {COMPLIANCE_FILTERS.map((filter) => (
                <button
                  className={`filter-tab ${
                    activeFilter === filter.value ? 'active' : ''
                  }`}
                  key={filter.value}
                  type='button'
                  onClick={() => setActiveFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className='advanced-filters'>
              <Select
                value={riskFilter}
                onChange={setRiskFilter}
                options={[
                  { label: 'Tous les risques', value: 'all' },
                  { label: 'Risque faible', value: 'low' },
                  { label: 'Risque moyen', value: 'medium' },
                  { label: 'Risque élevé', value: 'high' },
                  { label: 'Non calculé', value: 'missing' },
                ]}
              />
              <Select
                value={residenceFilter}
                onChange={setResidenceFilter}
                options={[
                  { label: 'Toutes résidences', value: 'all' },
                  ...residenceOptions,
                ]}
              />
            </div>
            <div className='toolbar-actions'>
              <Button className='action-button' onClick={resetFilters}>
                Réinitialiser
              </Button>
              <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
            </div>
          </section>

          <section className='compliance-workspace'>
            <div className='table-shell'>
              <div className='table-title'>
                <span>Registre conformité</span>
                <span className='table-count'>{displayClients.length} dossiers</span>
              </div>
              <Table
                columns={columns}
                dataSource={displayClients}
                rowClassName={(row, index) =>
                  `${index % 2 === 0 ? 'even-row' : 'odd-row'} ${
                    selectedClient?.id === row.id ? 'selected-row' : ''
                  }`
                }
                rowKey='id'
                size='small'
                loading={isLoading}
                scroll={{ x: 1120 }}
                pagination={{ pageSize: 10 }}
                onRow={(row) => ({
                  onClick: () => setSelectedClient(row),
                })}
              />
            </div>

            <aside className='compliance-detail'>
              <div className='detail-title'>Dossier conformité</div>
              {selectedClient ? (
                <div className='detail-body'>
                  <div className='detail-head'>
                    <div className='client-avatar'>{getInitials(selectedClient)}</div>
                    <div>
                      <h3>{getClientName(selectedClient)}</h3>
                      <span className='muted'>
                        {(selectedClient as any).code_referent || '--'}
                      </span>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Synthèse conformité
                      <span>{getDocumentStatus(selectedClient).completed}/{getDocumentStatus(selectedClient).total} docs</span>
                    </div>
                    <div className='detail-section-body risk-card'>
                      <div className='risk-metric'>
                        <span>Statut KYC</span>
                        <strong>
                          <StatusPill tone={getKycStatus(selectedClient).tone}>
                            {getKycStatus(selectedClient).label}
                          </StatusPill>
                        </strong>
                      </div>
                      <div className='risk-metric'>
                        <span>Score AML</span>
                        <strong>
                          <StatusPill tone={getAmlRisk(selectedClient).tone}>
                            {getAmlRisk(selectedClient).score}
                          </StatusPill>
                        </strong>
                      </div>
                      <div className='risk-metric'>
                        <span>Niveau AML</span>
                        <strong>
                          <StatusPill tone={getAmlRisk(selectedClient).tone}>
                            {getAmlRisk(selectedClient).label}
                          </StatusPill>
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Lecture risque
                      <span>{getRiskRecommendation(selectedClient).label}</span>
                    </div>
                    <div className='detail-section-body'>
                      <div className='detail-grid'>
                        <DetailRow
                          label='PPE'
                          value={(selectedClient as any).kyc?.politically_exposed ? 'Oui' : 'Non'}
                        />
                        <DetailRow
                          label='Pays de résidence'
                          value={(selectedClient as any).kyc?.country_of_residence || '--'}
                        />
                        <DetailRow
                          label='Revenu'
                          value={(selectedClient as any).kyc?.income_level || '--'}
                        />
                        <DetailRow
                          label='Objectif'
                          value={(selectedClient as any).kyc?.investment_purpose || '--'}
                        />
                      </div>
                      <div className='recommendation-box' style={{ marginTop: 10 }}>
                        {getRiskRecommendation(selectedClient).description}
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Identité déclarée
                      <span>Profil KYC</span>
                    </div>
                    <div className='detail-section-body detail-grid'>
                      <DetailRow
                        label='Nationalité'
                        value={(selectedClient as any).kyc?.nationality || '--'}
                      />
                      <DetailRow
                        label='Document identité'
                        value={(selectedClient as any).kyc?.id_type || '--'}
                      />
                      <DetailRow
                        label='Numéro pièce'
                        value={(selectedClient as any).kyc?.id_number || '--'}
                      />
                      <DetailRow
                        label='Adresse'
                        value={(selectedClient as any).kyc?.address || '--'}
                      />
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Documents
                      <span>Disponibilité des pièces</span>
                    </div>
                    <div className='detail-section-body document-list'>
                      {DOCUMENTS.map((doc) => {
                        const hasDocument = Boolean((selectedClient as any).kyc?.[doc.key]);
                        return (
                          <div className='document-link' key={doc.key}>
                            <span>{doc.label}</span>
                            {hasDocument ? (
                              <a
                                href={BASE_MEDIA + (selectedClient as any).kyc?.[doc.key]}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                <StatusPill tone='success'>Disponible</StatusPill>
                              </a>
                            ) : (
                              <StatusPill tone='danger'>Manquant</StatusPill>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className='hero-actions' style={{ marginTop: 14 }}>
                    <Button
                      className='primary-action'
                      onClick={() => setShowDrawer(true)}
                    >
                      Vérifier / mettre à jour
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='empty-detail'>
                  Sélectionnez un dossier pour voir le détail conformité.
                </div>
              )}
            </aside>
          </section>
        </div>

        <Drawer
          title='Vérifier le KYC / AML'
          className='mt-compliance-drawer'
          placement='right'
          width='min(100vw, 760px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <KYCForm
            clientToUpdate={selectedClient}
            setShowDrawer={setShowDrawer}
            getNewClient={upsertClient}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: number;
}) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);

const drawerStyles = css`
  .mt-compliance-drawer .ant-drawer-content {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .mt-compliance-drawer .ant-drawer-header {
    min-height: 64px;
    border-bottom: 1px solid rgba(255, 176, 0, 0.22);
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.16), transparent 40%),
      ${PRIMARY};
  }

  .mt-compliance-drawer .ant-drawer-title {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 16px;
    text-transform: uppercase;
  }

  .mt-compliance-drawer .ant-drawer-close {
    color: #fff7e8;
  }

  .mt-compliance-drawer .ant-drawer-body {
    padding: 18px;
    background:
      radial-gradient(
        circle at 12% 0%,
        rgba(255, 79, 31, 0.06),
        transparent 25%
      ),
      var(--mt-bg);
  }

  .ant-select-dropdown {
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background: var(--mt-card);
  }

  .ant-select-item {
    color: var(--mt-text);
  }

  .ant-select-item-option-selected,
  .ant-select-item-option-active {
    background: var(--mt-row-hover) !important;
    color: var(--mt-warning) !important;
  }
`;

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className='detail-row'>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const COMPLIANCE_FILTERS: { label: string; value: ComplianceFilter }[] = [
  { label: 'Tous', value: 'all' },
  { label: 'KYC vérifiés', value: 'verified' },
  { label: 'KYC manquants', value: 'missing' },
  { label: 'Risque élevé', value: 'highRisk' },
  { label: 'Docs manquants', value: 'documents' },
];

const DOCUMENTS = [
  { key: 'id_document_front', label: 'Pièce recto' },
  { key: 'id_document_back', label: 'Pièce verso' },
  { key: 'selfie', label: 'Selfie' },
  { key: 'proof_of_residency', label: 'Justificatif domicile' },
  { key: 'proof_of_revenue', label: 'Justificatif revenu' },
];

const normalizeRecord = (value: any) =>
  value && typeof value === 'object' && !value.error ? value : {};

const getClientName = (client: any) =>
  `${client?.nom || ''} ${client?.prenom || ''}`.trim() || 'Client sans nom';

const getInitials = (client: any) => {
  const nom = client?.nom?.[0] || '';
  const prenom = client?.prenom?.[0] || '';
  return `${nom}${prenom}`.toUpperCase() || 'CL';
};

const getKycStatus = (client: any) => {
  const status = client?.kyc?.status;

  if (!status) {
    return { label: 'Non soumis', type: 'missing' as const, tone: 'neutral' as StatusTone };
  }

  if (status === 'verified') {
    return { label: 'Vérifié', type: 'verified' as const, tone: 'success' as StatusTone };
  }

  if (status === 'rejected') {
    return { label: 'Rejeté', type: 'rejected' as const, tone: 'danger' as StatusTone };
  }

  if (status === 'submitted') {
    return { label: 'Soumis', type: 'submitted' as const, tone: 'warning' as StatusTone };
  }

  return { label: 'En attente', type: 'pending' as const, tone: 'warning' as StatusTone };
};

const getAmlRisk = (client: any) => {
  const level = client?.aml?.aml_risk_level;
  const score = client?.aml?.aml_risk_score ?? 'N/A';

  if (level === 'Eleve' || level === 'Élevé') {
    return { label: 'Élevé', score, type: 'high' as const, tone: 'danger' as StatusTone };
  }

  if (level === 'Moyen') {
    return { label: 'Moyen', score, type: 'medium' as const, tone: 'warning' as StatusTone };
  }

  if (level === 'Faible') {
    return { label: 'Faible', score, type: 'low' as const, tone: 'success' as StatusTone };
  }

  return { label: 'Non calculé', score, type: 'missing' as const, tone: 'neutral' as StatusTone };
};

const getDocumentStatus = (client: any) => {
  const completed = DOCUMENTS.filter((doc) => client?.kyc?.[doc.key]).length;
  const total = DOCUMENTS.length;

  if (completed === total) {
    return { completed, total, type: 'complete' as const, tone: 'success' as StatusTone };
  }

  if (completed > 0) {
    return { completed, total, type: 'partial' as const, tone: 'warning' as StatusTone };
  }

  return { completed, total, type: 'missing' as const, tone: 'danger' as StatusTone };
};

const getRiskRecommendation = (client: any) => {
  const risk = getAmlRisk(client);
  const isPep = Boolean(client?.kyc?.politically_exposed);
  const docs = getDocumentStatus(client);

  if (risk.type === 'high' || isPep) {
    return {
      label: 'Vigilance renforcée',
      description:
        'Le dossier nécessite une revue manuelle renforcée avant validation opérationnelle.',
    };
  }

  if (docs.type !== 'complete' || risk.type === 'medium') {
    return {
      label: 'Revue standard',
      description:
        'Le dossier peut avancer, mais les points incomplets doivent être levés avant validation finale.',
    };
  }

  if (risk.type === 'low') {
    return {
      label: 'Vigilance normale',
      description:
        'Le profil ne présente pas de signal majeur selon les informations actuellement disponibles.',
    };
  }

  return {
    label: 'Analyse à compléter',
    description:
      'Le score AML ou les données KYC sont insuffisants pour formuler une conclusion fiable.',
  };
};

const getUniqueOptions = (options: { label: string; value: string }[]) =>
  Array.from(new Map(options.map((option) => [option.value, option])).values());
